import { LitElement, css, html } from "lit";
import { property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { AsyncController } from "../../shared/controllers/async-controller";
import { postProductDetailsPage } from "../../api-sdk/v3/routes";
import { DEFAULT_BASE_URL, SUPPORTED_LANGUAGES, WIDGET_NAMES } from "../../api-sdk/constants";
import { LANGUAGES, TUrl } from "../../api-sdk/types";
import { MissingConfig } from "../../utils/beam-errors";
import { enforceConfig } from "../../shared/enforce-config";
import { _cssVariableMap } from "../../shared/fragments/css-variable-map";
import { localizeUserString } from "../../shared/localize";
import { _errorMessage } from "../../shared/fragments/error-message-template";
import { cssResponsiveUtils } from "../../shared/responsive";
import { defineCustomElement } from "../../shared/defineCustomElement";
import { cssReset } from "../../shared/css-reset";
import { defineCustomText, useCustomText } from "../../shared/fragments/custom-text";
import { defineCustomBorder, useCustomBorder } from "../../shared/fragments/custom-border";
// shoelace html tag imports
import "../../shared/shoelace-components";
import "@shoelace-style/shoelace/dist/components/tooltip/tooltip.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import { _info } from "../../shared/fragments/info";
import { promoPillLabelConfigDefaults } from "../../shared/components/promo-pill-label";
import {
  getPromoCodesFromCart,
  setPromoCodeInCookie,
  parseJsonStringArray,
  formatStoreAndReturnPromoCodes,
  setPromoCodesInLocalStorage,
  isEmptyPromoData,
} from "../../utils/promoManager";
import "../../shared/components/promo-pill-label";
import { BEAM_CART_COOKIE_NAME, TBeamCartLocalStorage } from "../../shared/cart-contents";
import { createScopedLocalStorage } from "../../utils/local-storage";
import { makeApiKeyHeader } from "../../utils/makeApiKeyHeader";
import { BeamPromoCodesStoredEvent, BeamWidgetStoredPromoCodesEvent } from "../../utils/events";
import { getBeamCartId } from "../../utils/cart";
import { strings } from "./strings";

type RequiredConfig = {
  apiKey: string;
  storeId: number;
};
type IAPIV3PostProductDetailsPage = Awaited<ReturnType<typeof postProductDetailsPage>>;

const IMAGE_URL_REMOTE_CONFIG_KEY = "--beam-ProductDetailsPage-imageUrl";

export class BeamProductDetailsPage extends LitElement {
  static tagName = "beam-product-details-page";

  @property({ type: String, reflect: true }) public baseUrl: TUrl = DEFAULT_BASE_URL;

  @property({ type: String, reflect: false }) apiKey?: RequiredConfig["apiKey"];

  @property({ type: Number }) storeId?: number;

  @property({ type: String }) public lang: LANGUAGES = "en";

  @property({ type: Boolean }) public draftConfig = false;

  @property({ type: Boolean }) public debug = false;

  @property({ type: String }) remoteProductIdentifier?: string;

  @property({ type: String }) public promoCodes?: string;

  @property({ type: String }) public domain?: string;

  private localStorage = createScopedLocalStorage(this as LitElement & RequiredConfig);

  @state() private pluginPromoCodes: string[] = [];

  get configLang() {
    return SUPPORTED_LANGUAGES[this.lang] || "en";
  }

  get parsedPromoCodes(): string[] {
    return parseJsonStringArray(this.promoCodes);
  }

  private handlePromoCodesStored = (evt: BeamPromoCodesStoredEvent) => {
    const promoCodesFromPlugin = (evt.detail.promoCodes?.unvalidatedPromoCodes ?? [])
      .map((c) => c.attributes?.value ?? c.attributes?.url ?? "")
      .sort();
    this.pluginPromoCodes = promoCodesFromPlugin;
  };

  private getManualPromoCodes(): string[] {
    // Priority 1: Widget passed discount codes
    if (this.parsedPromoCodes && this.parsedPromoCodes.length > 0) {
      return this.parsedPromoCodes;
    }

    // Priority 2: Cart fallback
    const cart = this.localStorage.getItemJson<TBeamCartLocalStorage>("cart");
    return cart ? getPromoCodesFromCart(cart) : [];
  }

  private async handleValidatedPromoCodes(response: IAPIV3PostProductDetailsPage) {
    if (response.promos?.validatedPromoCodes) {
      await Promise.all([
        setPromoCodesInLocalStorage({
          apiKey: this.apiKey,
          promoCodes: { validatedPromoCodes: response.promos.validatedPromoCodes, unvalidatedPromoCodes: [] },
        }),
        setPromoCodeInCookie({
          validatedPromoCodes: response.promos.validatedPromoCodes,
          domain: this.domain,
        }),
      ]);
      this.dispatchEvent(
        new BeamWidgetStoredPromoCodesEvent({
          source: WIDGET_NAMES.product_details_page,
        })
      );
    }
  }

  async firstUpdated() {
    window.addEventListener(BeamPromoCodesStoredEvent.eventName, this.handlePromoCodesStored as EventListener);
  }

  disconnectedCallback() {
    window.removeEventListener(BeamPromoCodesStoredEvent.eventName, this.handlePromoCodesStored as EventListener);
    super.disconnectedCallback();
  }

  postProductDetailsPageData = async () => {
    if (!enforceConfig<RequiredConfig>(["apiKey", "storeId"], this)) throw new MissingConfig();
    const beamCartId = getBeamCartId(BEAM_CART_COOKIE_NAME, { apiKey: this.apiKey });

    const manualPromoCodes = this.getManualPromoCodes();
    const formattedPromos = formatStoreAndReturnPromoCodes(
      manualPromoCodes.map((code) => ({ value: code })),
      this.apiKey
    );
    const shouldSendPromos = formattedPromos && !isEmptyPromoData(formattedPromos);

    if (shouldSendPromos) {
      this.dispatchEvent(
        new BeamWidgetStoredPromoCodesEvent({
          source: WIDGET_NAMES.product_details_page,
        })
      );
    }
    const res = await postProductDetailsPage({
      baseUrl: this.baseUrl,
      headers: makeApiKeyHeader(this.apiKey),
      queryParams: {
        version: "1.0.0",
        lang: this.configLang,
        storeId: this.storeId,
        widgetName: WIDGET_NAMES.product_details_page,
        draftConfig: this.draftConfig,
        remoteProductIdentifier: this.remoteProductIdentifier,
      },
      requestBody: {
        ...(shouldSendPromos && { promos: formattedPromos }),
        beamCartId: beamCartId ? beamCartId : undefined,
      },
    });
    this.handleValidatedPromoCodes(res);
    return res;
  };

  private productDetailsPageDataController = new AsyncController<typeof this.postProductDetailsPageData>(
    this,
    this.postProductDetailsPageData
  );

  async updated(changedProperties: Map<string, unknown>) {
    const requireNewDataProps = ["storeId", "baseUrl", "lang", "apiKey", "pluginPromoCodes", "promoCodes"];
    void this.pluginPromoCodes;
    for (const prop of requireNewDataProps) {
      if (changedProperties.has(prop)) {
        await this.productDetailsPageDataController.exec();
        break;
      }
    }
  }

  render() {
    const { data, loading } = this.productDetailsPageDataController;
    const iconDisplay = this.cssVariables["--beam-ProductDetailsPage-image-placement"];
    const isNonZero = (value: string) => value !== "0px" && value !== "0";
    const shouldDisplayPromoPill = this.cssVariables["--beam-ProductDetailsPage-shouldDisplayPromoPill"] === "true";
    const isPromoActive = !!data?.config?.web?.promo;
    const hasValidImageHeight = isNonZero(this.cssVariables["--beam-ProductDetailsPage-imageHeight"]);
    const hasValidTitleFontSize = isNonZero(this.cssVariables["--beam-ProductDetailsPage-title-fontSize"]);
    const isPromoPillVisible =
      shouldDisplayPromoPill && // Determines if the promo pill is enabled for the marketing version of the PDP
      isPromoActive && // Is promo currently active
      hasValidImageHeight && // Ensures the promo pill is not displayed for the subtext version of the PDP
      hasValidTitleFontSize; // Ensures the promo pill is not displayed for the subtext version of the PDP
    const shouldShowPunctuation = this.cssVariables["--beam-ProductDetailsPage-shouldDisplayColon"] === "true"; // Determines if a colon should be displayed in promo display

    if (loading) {
      return html``;
    }
    if (data == null) {
      if (this.debug) {
        return _errorMessage({ error: new Error("No data") });
      }
      return "";
    }
    return html`
      <style>
        :host {
          ${this.cssVariables.toCSS()}
        }
      </style>
      <div
        class="root ${iconDisplay === "block-center" ? "block-display" : "inline-display"}"
        aria-label="product detail page Beam component"
      >
        ${iconDisplay === "block-center"
          ? html`
              ${this.renderWidgetIcon(iconDisplay)}
              ${isPromoPillVisible
                ? html`
                    <div class="promo-container">
                      <beam-promo-info-pill .promo=${data?.config?.web?.promo}></beam-promo-info-pill>
                    </div>
                  `
                : html``}
              <div class="info-container">${this.renderContent(isPromoPillVisible, shouldShowPunctuation, data)}</div>
            `
          : html`
              ${isPromoPillVisible
                ? html`
                    <div class="promo-container">
                      <beam-promo-info-pill .promo=${data?.config?.web?.promo}></beam-promo-info-pill>
                    </div>
                  `
                : html``}
              <div class="content-inline">
                ${this.renderWidgetIcon(iconDisplay)}
                <div class="info-container">${this.renderContent(isPromoPillVisible, shouldShowPunctuation, data)}</div>
              </div>
            `}
      </div>
    `;
  }

  renderWidgetIcon(iconDisplay: string) {
    const imageUrl = this.cssVariables[IMAGE_URL_REMOTE_CONFIG_KEY];

    if (!imageUrl) return html``;

    const isBlockDisplay = iconDisplay === "block-center";
    const containerStyle = isBlockDisplay ? 'style="order:1"' : "";

    return html`
      <span class="icon-container" part="icon" ${containerStyle}>
        <img src=${imageUrl} part="icon" aria-hidden="true" alt="" />
      </span>
    `;
  }

  renderContent(isPromoPillVisible: boolean, shouldDisplayColon: boolean, data: IAPIV3PostProductDetailsPage) {
    const title = localizeUserString(this.configLang, data?.config?.web?.title) || strings[this.configLang].ctaTitle();
    const description =
      localizeUserString(this.configLang, data?.config?.web?.description) || strings[this.configLang].ctaMessage();
    return isPromoPillVisible
      ? html`
          <div class="info-inline-promo">
            <span class="title">${title + (shouldDisplayColon ? strings[this.configLang].inlineSeparator() : "")}</span>
            <span class="description"> <span>${description}</span> ${this.renderDisclosureTooltip()} </span>
          </div>
        `
      : html`
          <div class="title-block">
            <h3 class="title">${title}</h3>
          </div>
          <div class="description-block">
            <span class="description"> <span>${description}</span> ${this.renderDisclosureTooltip()} </span>
          </div>
        `;
  }

  renderCloseTooltipIcon() {
    return html`<svg
      aria-hidden="true"
      width="11"
      height="11"
      viewBox="0 0 11 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.40836 5.40811L1.48828 1.48804M5.40836 5.40811L9.32843 9.32819M5.40836 5.40811L9.32843 1.48804M5.40836 5.40811L1.48828 9.32819"
        stroke="white"
        stroke-width="1.56803"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg> `;
  }

  /* PayPal Giving Fund Disclosure */
  renderDisclosureTooltip() {
    let closeTimer: number | undefined;
    let ariaTimer: number | undefined;

    // Does the PDP module still have the focusElement of the page?
    const stillHasFocus = () => {
      return document.activeElement?.tagName.toLowerCase() === this.tagName.toLowerCase();
    };

    const onHide = () => {
      // Even after animation is done, we need to give the browser a moment
      // to move focus to another element before setting aria-hidden
      clearTimeout(ariaTimer);
      ariaTimer = setTimeout(() => {
        this.renderRoot.querySelector(".ppgf-disclosure-tooltip-content")?.setAttribute("aria-hidden", "true");
      }, 50);
    };

    const onShow = () => {
      clearTimeout(ariaTimer);
      this.renderRoot.querySelector(".ppgf-disclosure-tooltip-content")?.setAttribute("aria-hidden", "false");
    };

    // Close the tooltip
    const closeTooltip = ({
      skipDelay = false,
      keepOpenIfFocused = false,
    }: {
      skipDelay?: boolean;
      keepOpenIfFocused?: boolean;
    }) => {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(
        () => {
          // if focus is still on this module, don't close anything
          if (keepOpenIfFocused && stillHasFocus()) {
            return;
          }
          (this.renderRoot.querySelector(".ppgf-disclosure-tooltip") as any)?.hide();
        },
        skipDelay ? 0 : 300
      );
    };

    // Open the tooltip; resets the timeout for closing if one exists so we don't
    // close it again
    const openTooltip = () => {
      clearTimeout(closeTimer);
      (this.renderRoot.querySelector(".ppgf-disclosure-tooltip") as any)?.show();
    };

    // Toggle the tooltip
    const toggleTooltip = ({
      skipDelay = false,
      keepOpenIfFocused = false,
    }: {
      skipDelay?: boolean;
      keepOpenIfFocused?: boolean;
    }) => {
      const tooltip = this.renderRoot.querySelector(".ppgf-disclosure-tooltip") as any;
      if (tooltip) {
        if (tooltip.open) {
          closeTooltip({
            skipDelay,
            keepOpenIfFocused,
          });
        } else {
          openTooltip();
        }
      }
    };

    // Focus will go to "<sl-tooltip>" element first, triggering the tooltip to open
    const onFocusTrigger = () => {
      openTooltip();
    };

    // Blur on the trigger checks if focus is still on the PDP module before closing the tooltip
    const onBlurTrigger = () => {
      closeTooltip({
        skipDelay: false,
        keepOpenIfFocused: true,
      });
    };

    // If the icon is clicked, force open the tooltip (for mobile)
    const onClickTrigger = () => {
      openTooltip();
    };

    // Mouseover on the icon should open the tooltip every time
    const onMouseoverTooltip = () => {
      openTooltip();
    };

    // Mouseout of the icon should check if we came back within 100ms
    const onMouseoutTooltip = () => {
      closeTooltip({
        skipDelay: false,
        keepOpenIfFocused: false,
      });
    };

    // This is needed to close the tooltip if the focus goes from the tooltip content
    // to something outside the page
    const onKeydownTooltip = (evt: KeyboardEvent) => {
      // Check for Enter (key 13) or Space (key 32) which toggles the tooltip visibility immediately
      if (evt.key === "Enter" || evt.key === " ") {
        toggleTooltip({
          skipDelay: true,
          keepOpenIfFocused: false,
        });
        return;
      }

      // If we tab out of the PDP tooltip, allow the tooltip to close itself
      closeTooltip({
        skipDelay: true,
        keepOpenIfFocused: true,
      });
    };

    // If the X is clicked, force close the tooltip
    const onClickClose = () => {
      closeTooltip({
        skipDelay: true,
        keepOpenIfFocused: false,
      });
    };

    const ppgfDisclosure = this.productDetailsPageDataController.data?.ppgfDisclosure;
    return ppgfDisclosure
      ? html`<sl-tooltip
          class="ppgf-disclosure-tooltip"
          part="tooltip"
          trigger="manual"
          tabindex="0"
          @mouseenter="${onMouseoverTooltip}"
          @mouseleave="${onMouseoutTooltip}"
          @keydown="${onKeydownTooltip}"
          @sl-after-hide="${onHide}"
          @sl-after-show="${onShow}"
        >
          <div
            class="ppgf-disclosure-tooltip-content"
            id="BeamPPGFDisclosure"
            slot="content"
            part="tooltip-content"
            aria-hidden="true"
          >
            <button
              class="ppgf-disclosure-close"
              role="button"
              aria-label="Close tooltip"
              part="tooltip-close-button"
              @click="${onClickClose}"
            >
              ${this.renderCloseTooltipIcon()}
            </button>
            <div class="ppgf-disclosure-tooltip-copy" part="tooltip-disclosure-copy">
              ${unsafeHTML(ppgfDisclosure.copy)}
            </div>
          </div>
          <span
            class="ppgf-disclosure-tooltip-trigger"
            part="tooltip-disclosure-icon"
            aria-label="More Information"
            role="button"
            tabindex="0"
            @focus="${onFocusTrigger}"
            @blur="${onBlurTrigger}"
            @click="${onClickTrigger}"
          >
            <div class="ppgf-disclosure-tooltip-trigger-mask d-lg-none"></div>
            <sl-button
              variant="text"
              size="small"
              class="ppgf-disclosure-tooltip-trigger"
              part="tooltip-disclosure-button"
              aria-hidden="true"
              tabindex="-1"
              circle
            >
              ${_info()}
            </sl-button>
          </span>
        </sl-tooltip>`
      : html``;
  }
  /* End PayPal Giving Fund Disclosure */

  public get cssVariables() {
    const defaults = {
      "--beam-ProductDetailsPage-imageWidth": "24px",
      "--beam-ProductDetailsPage-imageHeight": "24px",
      "--beam-ProductDetailsPage-maxWidth": "346px",
      "--beam-ProductDetailsPage-paddingTop": "8px",
      "--beam-ProductDetailsPage-paddingRight": "12px",
      "--beam-ProductDetailsPage-paddingBottom": "8px",
      "--beam-ProductDetailsPage-paddingLeft": "15px",
      "--beam-ProductDetailsPage-InfoContainer-marginLeft": "8px",
      "--beam-ProductDetailsPage-InfoContainer-textAlign": "inherit",
      "--beam-ProductDetailsPage-InfoContainer-lineHeight": "15px",
      "--beam-ProductDetailsPage-InfoContainer-marginTop": "4px",
      "--beam-ProductDetailsPage-backgroundColor": "unset",
      "--beam-ProductDetailsPage-TooltipIcon-width": "10px",
      "--beam-ProductDetailsPage-TooltipIcon-color": "inherit",
      "--beam-ProductDetailsPage-PPGFDisclosure-backgroundColor": "#000000",
      "--beam-ProductDetailsPage-PPGFDisclosure-paddingTop": "10px",
      "--beam-ProductDetailsPage-PPGFDisclosure-paddingRight": "8px",
      "--beam-ProductDetailsPage-PPGFDisclosure-paddingBottom": "10px",
      "--beam-ProductDetailsPage-PPGFDisclosure-paddingLeft": "8px",
      "--beam-ProductDetailsPage-PPGFDisclosureHyperlink-color": "#5CA3FF",
      ...defineCustomText("--beam-ProductDetailsPage-title", {
        fontSize: "12px",
        fontWeight: "bold",
      }),
      "--beam-ProductDetailsPage-title-textAlign": "left",
      ...defineCustomText("--beam-ProductDetailsPage-description", {
        marginTop: "0px",
        fontSize: "12px",
        lineHeight: "15px",
      }),
      "--beam-ProductDetailsPage-description-textAlign": "left",
      ...defineCustomText("--beam-ProductDetailsPage-PPGFDisclosureTooltipContent", {
        fontSize: "12px",
        lineHeight: "16px",
        color: "#FFFFFF",
      }),
      ...defineCustomBorder("--beam-ProductDetailsPage"),
      "--beam-ProductDetailsPage-close-display": "initial",
      "--beam-ProductDetailsPage-close-padding": "0",
      "--beam-ProductDetailsPage-image-placement": "inline-left", // options: "inline-left" || "block-center"
      "--beam-ProductDetailsPage-blockImage-alignSelf": "center",
      "--beam-ProductDetailsPage-promoPill-marginTop": "4px",
      "--beam-ProductDetailsPage-promoPill-alignSelf": "left",
      "--beam-ProductDetailsPage-shouldDisplayColon": "true",
      "--beam-ProductDetailsPage-shouldDisplayPromoPill": "true",
      ...promoPillLabelConfigDefaults,
    };
    const remoteConfig = this.productDetailsPageDataController?.data?.config?.web?.theme || {};

    const config = { ...defaults, ...remoteConfig };

    const serializable = Object.create({
      toCSS() {
        return _cssVariableMap(this as Record<string, string>);
      },
    });

    return Object.assign(serializable, config);
  }

  static styles = [
    cssReset,
    cssResponsiveUtils,
    css`
      :host {
        font-family: var(--beam-fontFamily);
        font-style: var(--beam-fontStyle);
        font-size: var(--beam-fontSize);
        background-color: var(--beam-backgroundColor);
        color: var(--beam-textColor);
        max-width: var(--beam-ProductDetailsPage-maxWidth);
        word-break: normal;
        display: block;
      }
      .root {
        padding-top: var(--beam-ProductDetailsPage-paddingTop);
        padding-right: var(--beam-ProductDetailsPage-paddingRight);
        padding-bottom: var(--beam-ProductDetailsPage-paddingBottom);
        padding-left: var(--beam-ProductDetailsPage-paddingLeft);
        width: 100%;
        background-color: var(--beam-ProductDetailsPage-backgroundColor);
        display: flex;
        flex-direction: column;
        ${useCustomBorder("--beam-ProductDetailsPage")}
      }
      /* When iconDisplay is inline-center */
      .inline-display {
        display: flex;
      }
      /* When iconDisplay is block-center */
      .block-display {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
      .promo-container {
        order: 2;
        display: flex;
        margin-top: var(--beam-ProductDetailsPage-promoPill-marginTop);
        align-self: var(--beam-ProductDetailsPage-promoPill-alignSelf);
      }
      .content-inline {
        order: 3;
        display: flex;
        align-items: center;
      }
      /* Info block for both inline and block layout */
      .info-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        text-align: var(--beam-ProductDetailsPage-InfoContainer-textAlign);
        line-height: var(--beam-ProductDetailsPage-InfoContainer-lineHeight);
        margin-top: var(--beam-ProductDetailsPage-InfoContainer-marginTop);
        margin-left: var(--beam-ProductDetailsPage-InfoContainer-marginLeft);
      }
      .block-display .info-container {
        order: 3;
      }
      .root.inline-display .icon-container,
      .root.inline-display .info-container {
        display: inline-flex;
      }
      .root.inline-display {
        flex-direction: column;
      }
      .icon-container {
        width: var(--beam-ProductDetailsPage-imageWidth);
        height: var(--beam-ProductDetailsPage-imageHeight);
        align-self: var(--beam-ProductDetailsPage-blockImage-alignSelf);
        flex-shrink: 0;
      }
      .icon-container > img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
      .title {
        ${useCustomText("--beam-ProductDetailsPage-title")}
      }
      .title-block {
        text-align: var(--beam-ProductDetailsPage-title-textAlign);
        line-height: var(--beam-ProductDetailsPage-title-lineHeight);
      }
      .info-inline-promo {
        margin-top: var(--beam-ProductDetailsPage-description-marginTop, 0px);
      }
      .description {
        ${useCustomText("--beam-ProductDetailsPage-description")}
      }
      .description-block {
        text-align: var(--beam-ProductDetailsPage-description-textAlign);
        line-height: var(--beam-ProductDetailsPage-description-lineHeight);
        ${useCustomText("--beam-ProductDetailsPage-description")}
      }
      .ppgf-disclosure-tooltip {
        --max-width: 268px;
      }
      .ppgf-disclosure-tooltip::part(body) {
        background-color: var(--beam-ProductDetailsPage-PPGFDisclosure-backgroundColor);
        padding-top: var(--beam-ProductDetailsPage-PPGFDisclosure-paddingTop);
        padding-right: var(--beam-ProductDetailsPage-PPGFDisclosure-paddingRight);
        padding-bottom: var(--beam-ProductDetailsPage-PPGFDisclosure-paddingBottom);
        padding-left: var(--beam-ProductDetailsPage-PPGFDisclosure-paddingLeft);
      }
      .ppgf-disclosure-tooltip::part(base__arrow) {
        background-color: var(--beam-ProductDetailsPage-PPGFDisclosure-backgroundColor);
      }
      .ppgf-disclosure-tooltip-content {
        display: flex;
        flex-direction: column;
        ${useCustomText("--beam-ProductDetailsPage-PPGFDisclosureTooltipContent")}
      }
      .ppgf-disclosure-tooltip-copy > a,
      .ppgf-disclosure-close {
        pointer-events: auto;
        cursor: pointer;
        color: var(--beam-ProductDetailsPage-PPGFDisclosureHyperlink-color);
      }
      .ppgf-disclosure-close {
        display: var(--beam-ProductDetailsPage-close-display);
        padding: var(--beam-ProductDetailsPage-close-padding);
        height: 12px;
        background: none;
        border: none;
        margin-left: auto;
      }
      .ppgf-disclosure-tooltip-copy > a:visited {
        color: var(--beam-ProductDetailsPage-PPGFDisclosureHyperlink-color);
      }
      .ppgf-disclosure-tooltip-trigger {
        position: relative;
      }
      .ppgf-disclosure-tooltip-trigger-mask {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: calc(var(--beam-ProductDetailsPage-TooltipIcon-width) + 20px);
        height: calc(var(--beam-ProductDetailsPage-TooltipIcon-width) + 20px);
      }
      .ppgf-disclosure-tooltip-trigger::part(base) {
        line-height: 1;
        border: none;
        min-height: 0;
        min-width: 0;
        width: auto;
        color: var(--beam-ProductDetailsPage-TooltipIcon-color);
      }
      .ppgf-disclosure-tooltip-trigger::part(label) {
        padding: 0;
      }
      .ppgf-disclosure-tooltip-trigger > svg {
        width: var(--beam-ProductDetailsPage-TooltipIcon-width);
        color: var(--beam-ProductDetailsPage-TooltipIcon-color);
        height: auto;
      }
    `,
  ];
}

defineCustomElement(BeamProductDetailsPage);

declare global {
  interface HTMLElementTagNameMap {
    "beam-product-details-page": BeamProductDetailsPage;
  }
}
